import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import { IdleSignOut } from "@/components/IdleGuard";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { MeProvider, useMe } from "@/context/MeContext";
import Protected, { Loading } from "@/components/Protected";
import Journey, { ChooseOption } from "@/pages/Journey";
import { SignInPage, SignUpPage } from "@/pages/AuthPages";
import ProfileSetup from "@/pages/ProfileSetup";
import Baskets from "@/pages/Baskets";
import Ready from "@/pages/Ready";
import Questions from "@/pages/Questions";
import PlanView from "@/pages/PlanView";
import HomePage from "@/pages/Home";
import PlansPage from "@/pages/Plans";
import Explore from "@/pages/Explore";
import SettingsPage, { SecurityPage } from "@/pages/Settings";
import Membership, { PaymentCancel, PaymentSuccess } from "@/pages/Membership";
import Admin from "@/pages/AdminPortal";
import NotYet from "@/pages/NotYet";
import { HelpPage, PrivacyPage, TermsPage } from "@/pages/InfoPages";
import Notify from "@/pages/Notify";

function Root() {
  const { isLoaded, isSignedIn } = useAuth();
  const { me, loading } = useMe();
  if (!isLoaded) return <Loading />;
  if (!isSignedIn) return <Navigate to={localStorage.getItem("ahu_returning") ? "/sign-in" : "/start"} replace />;
  if (loading || !me) return <Loading />;
  if (!me.profile_complete) return <Navigate to="/profile-setup" replace />;
  if (me.role !== "admin" && me.access === "restricted") return <Navigate to="/settings" replace />;
  if (me.role !== "admin" && me.access === "lapsed") return <Navigate to="/membership" replace />;
  const intent = localStorage.getItem("ahu_intent");
  if (intent === "choose" && me.access === "free" && !me.plans_created) return <Navigate to="/choose" replace />;
  if (!me.is_member && (intent === "ahu_monthly" || intent === "ahu_annual")) return <Navigate to={`/membership?plan=${intent}`} replace />;
  if (sessionStorage.getItem("ahu_prompt")) return <Navigate to="/membership" replace />;
  if (me.plans_created === 0 && !me.baskets && me.role !== "admin") return <Navigate to="/baskets" replace />;
  return <HomePage />;
}

const P = (el, opts = {}) => <Protected {...opts}>{el}</Protected>;

function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <MeProvider>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/start/*" element={<Journey />} />
            <Route path="/choose" element={P(<ChooseOption />)} />
            <Route path="/signin" element={<Navigate to="/sign-in" replace />} />
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/notify" element={<Notify />} />
            <Route path="/profile-setup" element={P(<ProfileSetup />, { needProfile: false })} />
            <Route path="/baskets" element={P(<Baskets />)} />
            <Route path="/not-yet" element={P(<NotYet />)} />
            <Route path="/ready" element={P(<Ready />)} />
            <Route path="/questions" element={P(<Questions />)} />
            <Route path="/plans" element={P(<PlansPage />)} />
            <Route path="/plans/:id" element={P(<PlanView />)} />
            <Route path="/explore" element={P(<Explore />)} />
            <Route path="/settings" element={P(<SettingsPage />)} />
            <Route path="/settings/security/*" element={P(<SecurityPage />)} />
            <Route path="/membership" element={P(<Membership />)} />
            <Route path="/payment/success" element={P(<PaymentSuccess />)} />
            <Route path="/payment/cancel" element={P(<PaymentCancel />)} />
            <Route path="/admin" element={P(<Admin />, { admin: true })} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <IdleSignOut />
          <Toaster position="top-center" toastOptions={{ style: { fontFamily: "Work Sans, sans-serif", borderRadius: 14 } }} />
        </MeProvider>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

export default App;
